import { useState, useEffect } from 'react';
import apiService from '../../../../services/apiService';
import { useTenant } from '../../../../contexts/TenantContext';

const ThrowballChampionships = () => {
  const { siteSettings } = useTenant();
  const [championships, setChampionships] = useState({});
  const [years, setYears] = useState([]);
  const [activeYear, setActiveYear] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [city, setCity] = useState('');
  const [sport, setSport] = useState('');
  const [loading, setLoading] = useState(true);

  // Static/Default championships - shown when no dynamic data exists
  // No fallback championships. These were the founding federation's own
  // tournaments and appeared on any tenant that had none of its own.
  const staticChampionships = {};

  useEffect(() => {
    fetchChampionships();
  }, []);

  const fetchChampionships = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/public/championships');
      const data = response.data;

      if (data.success && data.data && Object.keys(data.data).length > 0) {
        setChampionships(data.data);
        setYears(data.years || Object.keys(data.data));
        setActiveYear(data.years?.[0] || Object.keys(data.data)[0]);
      } else {
        // Use static data as fallback
        setChampionships(staticChampionships);
        const staticYears = Object.keys(staticChampionships).sort((a, b) => b - a);
        setYears(staticYears);
        setActiveYear(staticYears[0]);
      }
    } catch (error) {
      console.error('Error fetching championships:', error);
      // Use static data as fallback
      setChampionships(staticChampionships);
      const staticYears = Object.keys(staticChampionships).sort((a, b) => b - a);
      setYears(staticYears);
      setActiveYear(staticYears[0]);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };


  // A full season is dozens of tournaments, so it is read a city or a sport at
  // a time. Both lists come from the season on screen, so neither ever offers a
  // choice that returns nothing.
  const season = championships[activeYear] || [];
  const cities = [...new Set(season.map((c) => c.city).filter(Boolean))].sort();
  const sports = [...new Set(season.map((c) => c.category).filter(Boolean))].sort();

  const shown = season.filter((c) =>
    (!city || c.city === city) && (!sport || c.category === sport));

  const chip = (on) => ({
    padding: '6px 14px',
    borderRadius: 20,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: on ? 700 : 500,
    border: `1px solid ${on ? '#a4262c' : '#d9dee5'}`,
    background: on ? '#a4262c' : '#fff',
    color: on ? '#fff' : '#42505f',
  });

  const FilterRow = ({ label, options, value, onPick }) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
      <span style={{
        fontSize: '0.7rem', letterSpacing: '0.09em', textTransform: 'uppercase',
        color: '#8b95a5', fontWeight: 800, minWidth: 46,
      }}>{label}</span>
      <button type="button" style={chip(!value)} onClick={() => { onPick(''); setOpenAccordion(null); }}>
        All
      </button>
      {options.map((option) => (
        <button key={option} type="button" style={chip(value === option)}
                onClick={() => { onPick(option); setOpenAccordion(null); }}>
          {option}
        </button>
      ))}
    </div>
  );

  return (
    <section className="tb-page-section tb-championships-section">
      <div className="tb-container">
        {/* Top Banner */}
        <div className="tb-page-banner">
          <img src={siteSettings?.page_banners?.championships || '/theme/assets/images/banner-2.jpg'} alt="Championships Banner" />
        </div>

        {/* Page Content */}
        <div className="tb-page-content">
          <div className="tb-heading-top">
            <h1 className="tb-page-title">Championships</h1>
            <span className="tb-mini-line"></span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading championships...</p>
            </div>
          ) : years.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No championship results available yet.</p>
            </div>
          ) : (
            <>
              {/* Tabs Navigation */}
              <div className="tb-tabs">
                <ul className="tb-tabs-nav">
                  {years.map((year) => (
                    <li
                      key={year}
                      className={`tb-tab-item ${activeYear === year ? 'active' : ''}`}
                      onClick={() => { setActiveYear(year); setOpenAccordion(null); }}
                    >
                      {year}
                    </li>
                  ))}
                </ul>

                {/* Tab Content */}
                <div className="tb-tabs-content">
                  {cities.length > 1 && (
                    <FilterRow label="City" options={cities} value={city} onPick={setCity} />
                  )}
                  {sports.length > 1 && (
                    <FilterRow label="Sport" options={sports} value={sport} onPick={setSport} />
                  )}

                  <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '14px 0 12px' }}>
                    {shown.length} {shown.length === 1 ? 'tournament' : 'tournaments'}
                    {city ? ` in ${city}` : ''}{sport ? ` · ${sport}` : ''}
                  </p>

                  {shown.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <p>No tournaments match those filters.</p>
                    </div>
                  ) : (
                    shown.map((champ) => (
                      <div className="tb-accordion" key={champ.id}>
                        <div
                          className={`tb-accordion-header ${openAccordion === champ.id ? 'active' : ''}`}
                          onClick={() => toggleAccordion(champ.id)}
                        >
                          <h4>{champ.title}</h4>
                          <i className={`ri-arrow-${openAccordion === champ.id ? 'up' : 'down'}-s-line`}></i>
                        </div>

                        {openAccordion === champ.id && (
                          <div className="tb-accordion-body">
                            <div className="tb-accordion-inner">
                              <div className="tb-accordion-row">
                                <div className="tb-accordion-col">
                                  <div className="tb-acc-info">
                                    <p><strong>Venue:</strong> {champ.venue}{champ.city ? `, ${champ.city}` : ''}{champ.state ? `, ${champ.state}` : ''}</p>
                                    <p><strong>Date:</strong> {champ.date}</p>
                                    {champ.level_name && <p><strong>Level:</strong> {champ.level_name}</p>}
                                    {champ.type_name && <p><strong>Type:</strong> {champ.type_name}</p>}
                                  </div>

                                  {champ.results && champ.results.length > 0 && (
                                    <div className="tb-table-wrapper">
                                      <table className="tb-info-table">
                                        <thead>
                                          <tr>
                                            <th className="tb-col-serial">Position</th>
                                            <th>State / Team</th>
                                            {champ.results.some(r => r.medal) && <th>Medal</th>}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {champ.results.map((result, idx) => (
                                            <tr key={idx}>
                                              <td className="tb-col-serial">
                                                {result.position === 1 && <span style={{ color: '#FFD700' }}><i className="ri-medal-fill"></i> </span>}
                                                {result.position === 2 && <span style={{ color: '#C0C0C0' }}><i className="ri-medal-fill"></i> </span>}
                                                {result.position === 3 && <span style={{ color: '#CD7F32' }}><i className="ri-medal-fill"></i> </span>}
                                                {result.position}
                                              </td>
                                              <td>
                                                {result.state}
                                                {result.team_name && result.team_name !== result.state && (
                                                  <span style={{ fontSize: '12px', color: '#666', display: 'block' }}>
                                                    ({result.team_name})
                                                  </span>
                                                )}
                                              </td>
                                              {champ.results.some(r => r.medal) && (
                                                <td>
                                                  {result.medal === 'gold' && <span style={{ color: '#FFD700' }}>Gold</span>}
                                                  {result.medal === 'silver' && <span style={{ color: '#C0C0C0' }}>Silver</span>}
                                                  {result.medal === 'bronze' && <span style={{ color: '#CD7F32' }}>Bronze</span>}
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>

                                {champ.image && (
                                  <div className="tb-accordion-col">
                                    <div className="tb-acc-image">
                                      <img src={champ.image} alt={champ.title} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ThrowballChampionships;
